import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Box,
  Page,
  Tabs,
  Tab,
  SidebarLayout,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton,
  Stack,
  Tooltip,
} from "@flukecorp-emu/common-ui";
import {
  TaxonomyList,
  TaxonomyDetail,
  TaxonomyForm,
} from "../../components/Taxonomy";
import taxonomyService from "../../services/taxonomyService";
import AddIcon from "@mui/icons-material/Add";

export const Route = createFileRoute("/_authenticated/taxonomy")({
  component: Taxonomy,
});

function Taxonomy() {
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Get data from taxonomy service
  const measures = taxonomyService.getAllMeasures();
  const sources = taxonomyService.getAllSources();

  const currentType = currentTab === 0 ? "measure" : "source";
  const currentItems = currentTab === 0 ? measures : sources;

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setSelectedItem(null);
    setEditingItem(null);
    setIsAdding(false);
  };

  const handleSelectItem = (item) => {
    setSelectedItem(item);
    setEditingItem(null);
    setIsAdding(false);
  };

  const handleAddItem = () => {
    setIsAdding(true);
    setEditingItem(null);
    setSelectedItem(null);
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setIsAdding(false);
  };

  const handleDeleteItem = (item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    // TODO: Implement actual delete logic
    console.log("Deleting item:", itemToDelete);

    // Close dialog and reset state
    setDeleteDialogOpen(false);
    setItemToDelete(null);

    // If the deleted item was selected, clear selection
    if (selectedItem?.code === itemToDelete?.code) {
      setSelectedItem(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleSaveItem = (formData) => {
    // TODO: Implement actual save logic
    console.log("Saving item:", formData);

    // Reset state
    setEditingItem(null);
    setIsAdding(false);

    // Select the saved item
    setSelectedItem(formData);
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setIsAdding(false);

    // If we were editing, restore the previous selection
    if (editingItem) {
      setSelectedItem(editingItem);
    }
  };

  return (
    <Page
      title="Taxonomy"
      sx={{ bgcolor: "common.white" }}
      disableGutters={true}
    >
      <Box
        sx={{
          height: "calc(100vh - 60px)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Master-Detail Layout with SidebarLayout */}
        <SidebarLayout
          collapsedWidth={60}
          height="100%"
          id="taxonomy-sidebar"
          maxWidth={600}
          minWidth={300}
          width={400}
          sidebarExpandTransitionDuration={0.3}
          sidebarHoverExpandTimeDelay={300}
          sidebarHeader={
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              {/* Tabs for Measures and Sources */}
              <Tabs value={currentTab} onChange={handleTabChange}>
                <Tab label="Measure" />
                <Tab label="Source" />
              </Tabs>
              <Tooltip title={`Add New ${currentType.toLocaleUpperCase()}`}>
                <IconButton
                  color="primary"
                  size="small"
                  onClick={handleAddItem}
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          }
          sidebarContent={
            <TaxonomyList
              items={currentItems}
              selectedItem={selectedItem}
              isAdding={isAdding}
              onSelectItem={handleSelectItem}
              onAddItem={handleAddItem}
              onEditItem={handleEditItem}
              onDeleteItem={handleDeleteItem}
              title={currentTab === 0 ? "Measure" : "Source"}
            />
          }
        >
          {/* Right Panel - Detail/Edit View */}

          {editingItem || isAdding ? (
            <TaxonomyForm
              item={editingItem}
              onSave={handleSaveItem}
              onCancel={handleCancelEdit}
              type={currentType}
            />
          ) : (
            <TaxonomyDetail
              item={selectedItem}
              onEdit={handleEditItem}
              type={currentType}
            />
          )}
        </SidebarLayout>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleCancelDelete}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              Are you sure you want to delete "{itemToDelete?.name}"? This
              action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete} color="primary">
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
              autoFocus
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Page>
  );
}
